/**
 * VertexSnap.js
 * 
 * This module provides functionality for vertex snapping in a 3D environment.
 * Vertex snapping is a common feature in CAD and 3D modeling applications that
 * helps users precisely position points by "snapping" to existing vertices when
 * the cursor is within a certain distance threshold.
 */

import {VERTICES} from "../../Global/variables";

/**
 * Distance threshold for vertex snapping in world units
 * If a point is within this distance of a vertex, it will snap to that vertex
 */
let threshold = 0.07;

/**
 * Finds the closest vertex to a given point within the snapping threshold
 * 
 * This function is used during operations like drawing or vertex manipulation
 * to determine if the cursor should snap to an existing vertex. It returns
 * the vertex to snap to, or null if no suitable vertex is found within the threshold.
 * 
 * The function first checks if we're already snapped to a vertex (prevVertexSnap)
 * and if the new point is still close enough to that vertex, we keep using it.
 * This provides stability and prevents "jumping" between close vertices.
 * 
 * If not snapped or the point has moved away from the previous snap vertex,
 * it searches all available vertices to find one within the threshold distance.
 * 
 * @param {Object|null} prevVertexSnap - The previously snapped vertex, if any
 * @param {THREE.Vector3} point - The current point/position to check for snapping
 * @returns {Object|null} The vertex to snap to, or null if no valid snap target found
 */
export function vertexSnap(prevVertexSnap, point) {
    // If we're already snapped to a vertex and still within threshold, keep using it
    if (prevVertexSnap && prevVertexSnap.position.distanceTo(point) <= threshold) {
        return prevVertexSnap;
    } else {
        // Not currently snapped, or moved away from previous snap vertex
        let found = false;
        // Get all available vertices from the global VERTICES array
        let vertices = VERTICES;
        
        // Loop through all vertices (organized in groups)
        for (let i = 0; i < vertices.length && !found; i++) {
            for (let j = 0; j < vertices[i].length && !found; j++) {
                // Calculate distance between current vertex and the point
                const d = vertices[i][j].position.distanceTo(point);
                
                // If the distance is within threshold, snap to this vertex
                if (d <= threshold) {
                    found = true;
                    return vertices[i][j];
                }
            }
        }
        
        // If no vertex is found within the threshold
        if (!found) {
            // If we were previously snapped to a vertex, hide it
            // by setting its material opacity to 0
            if (prevVertexSnap) {
                prevVertexSnap.material.opacity = 0;
            }
            // Return null to indicate no snapping should occur
            return null;
        }
    }
}